import type { Register } from './register.ts';

export type MemoryKey = 'memory-likes' | 'memory-does' | 'memory-has' | 'memory-person' | 'memory-event';

const KEYS: Record<string, MemoryKey> = {
  likes: 'memory-likes',
  does: 'memory-does',
  has: 'memory-has',
  person: 'memory-person',
};

/**
 * One Fluent message per kind rather than a label glued to the text, so a
 * translation puts the remembered words wherever its grammar wants them
 * (kid memory design §5). The text is English, so each message wraps it in
 * `<en>` for the card to draw as `<En>`.
 *
 * `<` and `>` are stripped because the text is model output and the catalog's
 * only markup is `<tag>…</tag>`: text must never open or close a marker.
 * A kind added by a newer server reads as the neutral event line rather than
 * a row the parent cannot delete.
 */
export function memoryLine(kind: string, text: string): { k: MemoryKey; params: { text: string } } {
  return { k: KEYS[kind] ?? 'memory-event', params: { text: text.replace(/[<>]/g, '') } };
}

/** The server's order is not part of its contract; the card shows newest first. */
export function newestFirst<T extends { createdAt: string }>(memories: readonly T[]): T[] {
  return [...memories].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
}

/**
 * Memories outlive a switch to an adult character, and this card is the only
 * place a parent can see or delete them (kid memory design §5), so it stays
 * while any are left.
 */
export function showMemoriesCard(register: Register, count: number): boolean {
  return register === 'kid' || count > 0;
}
